/**
 * Message Bubble Component
 *
 * Renders a single chat message with proper styling based on sender type.
 * Supports user messages, bot messages, and various Genesys message types.
 */

import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { baseStyles, messageBubbleStyles } from '../styles/shared-styles.js';

// Configure marked for safe, synchronous rendering
const renderer = new marked.Renderer();
renderer.link = ({ href, title, text }) => {
    const titleAttr = title ? ` title="${title}"` : '';
    return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
};
marked.setOptions({ async: false, breaks: true, gfm: true, renderer });

// Configure DOMPurify to allow safe HTML elements
const DOMPURIFY_CONFIG = {
    ALLOWED_TAGS: [
        'a',
        'b',
        'i',
        'strong',
        'em',
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'code',
        'pre',
        'span',
        'div',
        'button',
        // SVG elements for icons
        'svg',
        'path',
        'circle',
        'rect',
        'line',
        'polyline',
        'polygon',
        'g'
    ],
    ALLOWED_ATTR: [
        'href',
        'target',
        'rel',
        'class',
        'id',
        'style',
        'data-id',
        // SVG attributes
        'xmlns',
        'width',
        'height',
        'viewBox',
        'fill',
        'stroke',
        'stroke-width',
        'stroke-linecap',
        'stroke-linejoin',
        'd',
        'cx',
        'cy',
        'r',
        'x',
        'y',
        'rx',
        'ry',
        'x1',
        'y1',
        'x2',
        'y2',
        'points'
    ],
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ['target', 'rel'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea'],
    FORBID_ATTR: [
        'onerror',
        'onload',
        'onclick',
        'onmouseover',
        'onmouseout',
        'onfocus',
        'onblur',
        'onchange',
        'onsubmit',
        'onkeydown',
        'onkeyup',
        'onkeypress'
    ]
};
import { MESSAGE_TYPES } from '../stores/chat.store.js';
import './feedback-buttons.js';

/**
 * MessageBubble - Individual message renderer
 * @element message-bubble
 * @fires feedback - When feedback button is clicked
 */
export class MessageBubble extends LitElement {
    static properties = {
        message: { type: Object },
        showFeedback: { type: Boolean, attribute: 'show-feedback' },
        isFirstInGroup: { type: Boolean, attribute: 'is-first-in-group' },
        isLastInGroup: { type: Boolean, attribute: 'is-last-in-group' },
        _loadingMessageIndex: { type: Number, state: true }
    };

    static get LOADING_MESSAGES() {
        return [
            'Searching the planning rules\u2026',
            'Cross-referencing your property details\u2026',
            'Almost there\u2026'
        ];
    }

    static styles = [
        baseStyles,
        messageBubbleStyles,
        css`
            :host {
                display: block;
                width: 100%;
            }

            .message-container {
                display: flex;
                flex-direction: column;
                min-width: 0;
                flex: 1;
            }

            .message--user .message-container {
                max-width: 80%;
            }

            .message__content {
                word-wrap: break-word;
                overflow-wrap: break-word;
                overflow: hidden;
            }

            .message__content a {
                word-break: break-all;
            }

            .message__content ul,
            .message__content ol {
                padding-left: 20px;
                margin: 8px 0;
            }

            .message__content ul {
                list-style-type: disc;
            }

            .message__content ol {
                list-style-type: decimal;
            }

            .message__content li {
                margin: 2px 0;
            }

            .message__content p {
                margin: 8px 0;
            }

            .message__content p:first-child {
                margin-top: 0;
            }

            .message__content p:last-child {
                margin-bottom: 0;
            }

            .message__content strong {
                font-weight: 600;
            }

            .message__content a {
                color: #337ab7;
                text-decoration: none;
                cursor: pointer;
            }

            .message__content a:hover {
                color: #337ab7;
                text-decoration: none;
            }

            .message__content pre {
                background-color: rgba(0, 0, 0, 0.1);
                padding: 8px;
                border-radius: 4px;
                overflow-x: auto;
                font-size: 12px;
                margin: 8px 0;
            }

            .message__content code {
                font-family: 'Consolas', 'Monaco', monospace;
            }

            /* Streaming cursor - only show when there's content */
            .message--streaming.has-content .message__content::after {
                content: '▋';
                animation: blink 1s infinite;
            }

            @keyframes blink {
                0%,
                50% {
                    opacity: 1;
                }
                51%,
                100% {
                    opacity: 0;
                }
            }

            /* Typing indicator inside streaming message (when no content yet) */
            /* Original typing dots - used for Genesys live agent */
            .typing-dots {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 5px;
                min-height: calc(var(--font-size-base, 14px) * 1.2);
            }

            .typing-dots__dot {
                width: 7px;
                height: 7px;
                background-color: #90949c;
                border-radius: 50%;
                animation: typingBounce 1.4s infinite ease-in-out both;
            }

            .typing-dots__dot:nth-child(1) {
                animation-delay: -0.32s;
            }

            .typing-dots__dot:nth-child(2) {
                animation-delay: -0.16s;
            }

            .typing-dots__dot:nth-child(3) {
                animation-delay: 0s;
            }

            @keyframes typingBounce {
                0%,
                80%,
                100% {
                    transform: scale(0.6);
                    opacity: 0.5;
                }
                40% {
                    transform: scale(1);
                    opacity: 1;
                }
            }

            /* DAISY thinking indicator - bouncing dots + fade-in text in bubble */
            .thinking-indicator {
                display: flex;
                align-items: flex-start;
                gap: 10px;
                background-color: var(--bg-bot-message);
                border-radius: 9px;
                padding: 12px;
                width: 100%;
            }

            .thinking-indicator__dots {
                display: flex;
                align-items: center;
                gap: 4px;
                padding-top: 5px;
                flex-shrink: 0;
            }

            .thinking-indicator__dot {
                width: 6px;
                height: 6px;
                border-radius: 9999px;
                background-color: #90949c;
                display: inline-block;
                animation: thinkingBounce 1.2s infinite ease-in-out;
            }

            .thinking-indicator__dot:nth-child(2) {
                animation-delay: 150ms;
            }

            .thinking-indicator__dot:nth-child(3) {
                animation-delay: 300ms;
            }

            @keyframes thinkingBounce {
                0%, 60%, 100% { transform: translateY(0);    opacity: 0.45; }
                30%           { transform: translateY(-3px); opacity: 1;    }
            }

            .thinking-indicator__text {
                font-size: 13px;
                font-weight: 500;
                line-height: 1.4;
                color: #90949c;
                word-break: break-word;
                min-width: 0;
                flex: 1;
                animation: thinkingFade 420ms ease-out both;
            }

            @keyframes thinkingFade {
                0%   { opacity: 0; }
                100% { opacity: 1; }
            }

            @media (prefers-reduced-motion: reduce) {
                .thinking-indicator__dot,
                .thinking-indicator__text {
                    animation: none;
                }
            }

            .message--genesys-agent .message__bubble,
            .message--genesys-bot .message__bubble {
                background-color: #e3f2fd;
            }

            .message--genesys-status .message__bubble {
                background-color: #f5f5f5;
                font-style: italic;
                text-align: center;
                border: 1px dashed #ccc;
            }

            .message__feedback {
                margin-top: 5px;
            }

            /* Avatar placeholder for grouped messages (maintains alignment) */
            .message__avatar-placeholder {
                width: 32px;
                height: 0;
                margin-right: var(--spacing-sm);
                flex-shrink: 0;
            }

            /* Grouped messages - tighter spacing for consecutive bot messages */
            .message--grouped {
                margin-top: -6px;
            }

            .message--grouped .message__bubble {
                border-top-left-radius: var(--border-radius-lg);
            }

            .message__avatar--with-meta {
                margin-top: 0;
            }

            /* Styles for "Send message to council" button rendered via unsafeHTML */
            .message__content .notify-council-btn {
                width: 100%;
                padding: 12px 16px;
                background: #111111;
                color: #ffffff;
                border: none;
                border-radius: 10px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: background-color 0.2s ease;
            }

            .message__content .notify-council-btn:hover {
                background-color: #333333;
            }

            .message__meta {
                display: flex;
                align-items: baseline;
                gap: 8px;
                margin-bottom: 4px;
                line-height: 1;
            }

            .message__sender {
                font-size: 12px;
                font-weight: 600;
                color: var(--text-secondary);
            }

            .message__time {
                font-size: 11px;
                color: var(--text-secondary);
                opacity: 0.7;
            }
        `
    ];

    constructor() {
        super();
        this.message = null;
        this.showFeedback = false;
        this.isFirstInGroup = true;
        this.isLastInGroup = true;
        this._loadingMessageIndex = 0;
        this._loadingInterval = null;
    }

    render() {
        if (!this.message) return null;

        const { text, type, sender, timestamp, persistedMessageId, feedback } = this.message;
        const isUser = sender === 'user';
        const isStreaming = type === MESSAGE_TYPES.STREAMING;
        const hasContent = text && text.trim().length > 0;
        const messageClass = this._getMessageClass(type, isUser);
        const selectedFeedback = feedback === 'good' ? 'up' : feedback === 'bad' ? 'down' : null;

        // Determine if this is part of a group (consecutive bot messages)
        const isGroupedMessage = !isUser && !this.isFirstInGroup;
        const groupClass = isGroupedMessage ? 'message--grouped' : '';
        // Add has-content class for streaming cursor visibility
        const contentClass = isStreaming && hasContent ? 'has-content' : '';

        // DAISY thinking indicator: render standalone outside the bubble
        const isDaisyThinking = isStreaming && !hasContent && this.message?.genesysSessionId == null;

        // When DAISY is thinking, render bouncing dots + loading message in a bubble
        if (isDaisyThinking) {
            return html`
                <div class="message ${messageClass} ${groupClass}">
                    <div class="message__avatar-placeholder"></div>
                    <div class="message-container">
                        <div class="thinking-indicator">
                            <div class="thinking-indicator__dots" aria-hidden="true">
                                <span class="thinking-indicator__dot"></span>
                                <span class="thinking-indicator__dot"></span>
                                <span class="thinking-indicator__dot"></span>
                            </div>
                            <span class="thinking-indicator__text">${this._getCurrentLoadingMessage()}</span>
                        </div>
                    </div>
                </div>
            `;
        }

        return html`
            <div class="message ${messageClass} ${groupClass} ${contentClass}">
                ${!isUser
                    ? html`
                          ${this.isFirstInGroup
                              ? html`
                                    <img
                                        class="message__avatar ${this._shouldShowMeta(type)
                                            ? 'message__avatar--with-meta'
                                            : ''}"
                                        src="./Daisy_Updated.png"
                                        alt="DAISY"
                                        @error=${this._handleImageError}
                                    />
                                `
                              : html` <div class="message__avatar-placeholder"></div> `}
                      `
                    : ''}

                <div class="message-container">
                    ${this._shouldShowMeta(type) && this.isFirstInGroup
                        ? html`
                              <div class="message__meta">
                                  <span class="message__sender">${this._getSenderName(type, sender)}</span>
                                  ${timestamp
                                      ? html` <span class="message__time">${this._formatTime(timestamp)}</span> `
                                      : ''}
                              </div>
                          `
                        : ''}

                    <div class="message__bubble">
                        <div class="message__content">
                            ${isStreaming && !hasContent
                                ? html`<div class="typing-dots">
                                      <span class="typing-dots__dot"></span>
                                      <span class="typing-dots__dot"></span>
                                      <span class="typing-dots__dot"></span>
                                  </div>`
                                : this._renderContent(text)}
                        </div>
                    </div>

                    ${this.showFeedback && !isUser && !isStreaming && this.isLastInGroup && persistedMessageId
                        ? html`
                              <div class="message__feedback">
                                  <feedback-buttons
                                      .messageId=${persistedMessageId}
                                      .selected=${selectedFeedback}
                                      @feedback=${this._handleFeedback}
                                  >
                                  </feedback-buttons>
                              </div>
                          `
                        : ''}
                </div>
            </div>
        `;
    }

    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('message')) {
            const isStreaming = this.message?.type === MESSAGE_TYPES.STREAMING;
            const hasContent = this.message?.text?.trim()?.length > 0;
            // Only show loading messages for DAISY bot streaming, not Genesys live agent
            const isGenesys = this.message?.genesysSessionId != null;
            if (isStreaming && !hasContent && !isGenesys) {
                this._startLoadingMessageCycle();
            } else {
                this._stopLoadingMessageCycle();
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._stopLoadingMessageCycle();
    }

    _startLoadingMessageCycle() {
        if (this._loadingInterval) return;
        this._loadingMessageIndex = 0;
        const messages = MessageBubble.LOADING_MESSAGES;
        const lastIndex = messages.length - 1;

        this._loadingInterval = setInterval(() => {
            if (this._loadingMessageIndex < lastIndex) {
                this._loadingMessageIndex++;
            } else {
                clearInterval(this._loadingInterval);
                this._loadingInterval = null;
            }
        }, 3500);
    }

    _stopLoadingMessageCycle() {
        if (this._loadingInterval) {
            clearInterval(this._loadingInterval);
            this._loadingInterval = null;
        }
        this._loadingMessageIndex = 0;
    }

    _getCurrentLoadingMessage() {
        const messages = MessageBubble.LOADING_MESSAGES;
        return messages[Math.min(this._loadingMessageIndex, messages.length - 1)];
    }

    /**
     * Get CSS class for message type
     */
    _getMessageClass(type, isUser) {
        const classes = [isUser ? 'message--user' : 'message--bot'];

        switch (type) {
            case MESSAGE_TYPES.STREAMING:
                classes.push('message--streaming');
                break;
            case MESSAGE_TYPES.GENESYS_AGENT:
                classes.push('message--genesys-agent');
                break;
            case MESSAGE_TYPES.GENESYS_BOT:
                classes.push('message--genesys-bot');
                break;
            case MESSAGE_TYPES.GENESYS_STATUS:
                classes.push('message--genesys-status');
                break;
            case MESSAGE_TYPES.GENESYS_USER:
                classes.push('message--genesys-user');
                break;
        }

        return classes.join(' ');
    }

    /**
     * Determine if meta info should be shown
     */
    _shouldShowMeta(type) {
        return [
            MESSAGE_TYPES.BOT,
            MESSAGE_TYPES.STREAMING,
            MESSAGE_TYPES.GENESYS_AGENT,
            MESSAGE_TYPES.GENESYS_BOT,
            MESSAGE_TYPES.GENESYS_STATUS
        ].includes(type);
    }

    /**
     * Get display name for sender
     */
    _getSenderName(type, sender) {
        switch (type) {
            case MESSAGE_TYPES.GENESYS_AGENT:
                return this.message?.agentName || 'Live Agent';
            case MESSAGE_TYPES.GENESYS_BOT:
                return this.message?.agentName || 'Live Agent';
            case MESSAGE_TYPES.GENESYS_STATUS:
                return 'Status';
            default:
                return sender === 'user' ? 'You' : 'DAISY';
        }
    }

    /**
     * Handle image load error
     */
    _handleImageError(e) {
        e.target.style.display = 'none';
    }

    /**
     * Render message content with markdown parsing and HTML sanitization
     */
    _renderContent(text) {
        if (!text) return '';

        // Parse Markdown while preserving inline HTML emitted by the stream
        // (for example decorated links), then sanitize the combined output.
        const processed = marked.parse(text);

        // Sanitize HTML to prevent XSS attacks
        let sanitized = DOMPurify.sanitize(processed, DOMPURIFY_CONFIG);

        // Strip leading <p> and trailing </p> to avoid double-paragraph spacing inside <span>
        // Inner </p><p> pairs are preserved as paragraph breaks
        sanitized = sanitized.replace(/^\s*<p>/, '').replace(/<\/p>\s*$/, '');

        return html`<span>${unsafeHTML(sanitized)}</span>`;
    }

    /**
     * Format timestamp for display
     */
    _formatTime(timestamp) {
        try {
            const date = new Date(timestamp);
            return date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return '';
        }
    }

    /**
     * Handle feedback button click
     */
    _handleFeedback(e) {
        this.dispatchEvent(
            new CustomEvent('feedback', {
                detail: {
                    messageId: this.message.id,
                    persistedMessageId: e.detail.messageId,
                    feedback: e.detail.feedback
                },
                bubbles: true,
                composed: true
            })
        );
    }
}

customElements.define('message-bubble', MessageBubble);
